/** WebSocket lifecycle (ConnectionManager) + client-side sort helpers. */

import { io, Socket } from 'socket.io-client';
import { hydrateTimestamps } from './hydrate';
import type { Protocol } from './protocol';

export const BACKEND_URL = (() => {
  // Vite apps: VITE_BACKEND_URL is inlined at build time.
  const viteUrl = (import.meta as unknown as { env?: Record<string, string> })?.env
    ?.VITE_BACKEND_URL as string | undefined;
  if (viteUrl) return viteUrl.replace(/\/+$/, '');
  return '';
})();

/**
 * Robust WebSocket Lifecycle Management
 */
export class ConnectionManager {
  private socket: Socket;
  private subscriptions: Map<
    string,
    { tableName: string; options: Protocol.QueryOptions | null; isGroup: boolean }
  > = new Map();
  private identity: { token: string } | null = null;
  private isConnected: boolean = false;

  constructor() {
    this.socket = io(BACKEND_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.socket.on('connect', () => {
      console.log('[RethinkClient] Connected to backend');
      this.isConnected = true;
      this.resubscribe();
    });

    this.socket.on('disconnect', (reason) => {
      console.warn('[RethinkClient] Disconnected:', reason);
      this.isConnected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('[RethinkClient] Connection error:', error.message);
      if (error.message === 'xhr poll error') {
        console.warn('[RethinkClient] Polling failed, retrying with websocket...');
      }
    });
  }

  /**
   * Update the auth identity used for socket subscriptions. Called when the
   * Firebase ID token (re)resolves — e.g. right after a page refresh, where the
   * initial anonymous subscribe would otherwise be rejected by the server.
   * Re-emits every active subscription so authorized feeds get established.
   */
  setIdentity(identity: { token: string } | null) {
    this.identity = identity;
    if (this.isConnected) {
      this.resubscribe();
    }
  }

  private resubscribe() {
    if (this.subscriptions.size > 0) {
      console.log(`[RethinkClient] Resubscribing to ${this.subscriptions.size} subscriptions`);
      this.subscriptions.forEach((subscription, subscriptionKey) => {
        this.socket.emit(
          'subscribe',
          subscriptionKey,
          subscription.tableName,
          this.identity ? { token: this.identity.token } : undefined,
          subscription.options || {},
          subscription.isGroup,
        );
      });
    }
  }

  subscribe(
    table: string,
    options: Protocol.QueryOptions | null,
    isGroup: boolean,
    callback: (change: Protocol.ChangeEvent) => void,
  ) {
    // Two live queries on the same collection with different filters must get
    // distinct server-side subscriptions, so include a stable options fingerprint
    // in the key. The server treats this key as opaque.
    const subscriptionKey = `${isGroup ? 'group:' : ''}${table}|${JSON.stringify(options || {})}`;
    this.subscriptions.set(subscriptionKey, { tableName: table, options, isGroup });
    if (this.isConnected) {
      this.socket.emit(
        'subscribe',
        subscriptionKey,
        table,
        this.identity ? { token: this.identity.token } : undefined,
        options || {},
        isGroup,
      );
    }

    const eventName = `change:${subscriptionKey}`;

    const wrappedCallback = (change: Protocol.ChangeEvent) => {
      const hydratedChange = {
        ...change,
        old_val: change.old_val ? hydrateTimestamps(change.old_val) : undefined,
        new_val: change.new_val ? hydrateTimestamps(change.new_val) : undefined,
      };
      callback(hydratedChange);
    };

    this.socket.on(eventName, wrappedCallback);

    return () => {
      this.socket.off(eventName, wrappedCallback);
      this.subscriptions.delete(subscriptionKey);
      if (this.isConnected) {
        this.socket.emit('unsubscribe', subscriptionKey);
      }
    };
  }

  getSocket() {
    return this.socket;
  }
}

export function compareDocs(a: any, b: any, orderBy: Protocol.OrderBy[]) {
  const criteria =
    orderBy.length > 0 ? orderBy : [{ field: 'id', direction: 'asc' } as Protocol.OrderBy];

  for (const { field, direction } of criteria) {
    const valA = field.split('.').reduce((acc, part) => acc && acc[part], a);
    const valB = field.split('.').reduce((acc, part) => acc && acc[part], b);

    if (valA < valB) return direction === 'asc' ? -1 : 1;
    if (valA > valB) return direction === 'asc' ? 1 : -1;
  }
  return 0;
}

export function findInsertionIndex(array: any[], item: any, orderBy: Protocol.OrderBy[]): number {
  let low = 0;
  let high = array.length;

  while (low < high) {
    const mid = (low + high) >>> 1;
    if (compareDocs(array[mid], item, orderBy) < 0) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}
